import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { track } from '../analytics/analytics';
import { EVENTS } from '../analytics/events';
import {
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithCustomToken,
  GoogleAuthProvider,
  getMultiFactorResolver,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  RecaptchaVerifier,
} from 'firebase/auth';
import { auth } from '../firebase/config';
import { API_ENDPOINTS, apiClient } from '../config/api';
import { loadProspectStages } from '../services/prospectStageCache';
import {
  loginWithPasskey,
  isWebAuthnSupported,
  isPlatformAuthenticatorAvailable,
  hasEnrolledPasskey,
  hasDismissedPasskeyPrompt,
  dismissPasskeyPrompt,
  enrollPasskey,
  WEB_PASSKEY_FLAG,
} from '../services/webauthn.service';
import { initWebPush } from '../services/webPushService';
import { getActiveRoleFromStorage } from '../utils/getActiveRole';
import './LoginPage.css';

// Where to land after login. Service Providers go straight to their own
// dashboard: routing them through /dashboard first starts DashboardPage's
// user-data fetch, whose late redirect to /dashboard/sp could bounce them off
// whatever page they'd already clicked. Every other role keeps /dashboard.
const getPostLoginHome = () =>
  getActiveRoleFromStorage() === 'SERVICE_PROVIDER' ? '/dashboard/sp' : '/dashboard';

// ── MFA helpers ────────────────────────────────────────────────────────────────

const MFA_OTP_LENGTH = 6;
const MFA_RESEND_TIMER_SECONDS = 60;
const MFA_RECAPTCHA_TIMEOUT_MS = 120000;

const getWebDeviceName = () => {
  const ua = navigator.userAgent;
  if (/Edg\//.test(ua)) return 'Edge Browser';
  if (/OPR\/|Opera\//.test(ua)) return 'Opera Browser';
  if (/Chrome\//.test(ua)) return 'Chrome Browser';
  if (/Firefox\//.test(ua)) return 'Firefox Browser';
  if (/Safari\//.test(ua)) return 'Safari Browser';
  return 'Web Browser';
};

// Removes a stray reCAPTCHA v2 challenge overlay left behind after a timeout/fallback.
const removeStrayMfaRecaptcha = () => {
  document.querySelectorAll('body > div').forEach((el) => {
    if (el.id !== 'root' && el.querySelector('iframe[src*="recaptcha/api2/bframe"]')) {
      el.remove();
    }
  });
};

// ── Component ──────────────────────────────────────────────────────────────────

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const prefillEmail = location.state?.prefillEmail || '';
  const verificationComplete = location.state?.verificationComplete || false;
  const pendingInviteAccept = location.state?.pendingInviteAccept || false;

  useEffect(() => {
    const isAuthenticated = localStorage.getItem('isAuthenticated') === 'true';
    const user = localStorage.getItem('user');
    if (isAuthenticated && user) {
      navigate(getPostLoginHome(), { replace: true });
    }
  }, [navigate]);

  const [formData, setFormData] = useState({ email: prefillEmail, password: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [status, setStatus] = useState({ type: '', message: '' });

  // Show "Sign in with Fingerprint" only when THREE conditions hold:
  //   (1) browser supports WebAuthn,
  //   (2) the device actually has a built-in biometric authenticator
  //       (Touch ID / Windows Hello / Android fingerprint), and
  //   (3) this browser has previously enrolled a passkey for some account.
  // Rationale: showing the button on a device without biometrics or on a
  // fresh browser leaves the user to click it and hit an unhelpful "No
  // passkey found" error. Post-login enrollment prompt (below) restores
  // the button once the user enrolls on this browser.
  const [showPasskey, setShowPasskey] = useState(false);
  useEffect(() => {
    (async () => {
      if (!isWebAuthnSupported()) return;
      const hasBiometric = await isPlatformAuthenticatorAvailable();
      if (!hasBiometric) return;
      setShowPasskey(hasEnrolledPasskey());
    })();
  }, []);

  // Post-login enrollment prompt state — offered right after a successful
  // password sign-in when the device can do biometrics but this browser
  // hasn't enrolled a passkey yet. Skips silently on non-biometric devices.
  const [enrollPrompt, setEnrollPrompt] = useState(null); // { navigateTo } | null
  const [enrolling, setEnrolling] = useState(false);
  const [enrollError, setEnrollError] = useState('');
  // Off by default: dismissing without ticking this suppresses the prompt on
  // future logins from this browser. Ticked = user wants the reminder later.
  const [enrollDontAsk, setEnrollDontAsk] = useState(false);

  // Mobile app banner
  const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
  const [showAppBanner, setShowAppBanner] = useState(
    isMobile && sessionStorage.getItem('appBannerDismissed') !== 'true'
  );

  useEffect(() => {
    if (showAppBanner) {
      document.body.classList.add('app-banner-visible');
    } else {
      document.body.classList.remove('app-banner-visible');
    }
    return () => document.body.classList.remove('app-banner-visible');
  }, [showAppBanner]);

  const dismissBanner = () => {
    sessionStorage.setItem('appBannerDismissed', 'true');
    setShowAppBanner(false);
  };
  const openApp = () => {
    const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
    const isAndroid = /Android/i.test(navigator.userAgent);
    if (isIOS) {
      window.location.href = 'rialty://';
      setTimeout(() => { window.location.href = 'https://apps.apple.com/app/id6761278365'; }, 2000);
    } else if (isAndroid) {
      window.location.href = 'intent://#Intent;scheme=rialty;package=ai.rialty.app;S.browser_fallback_url=https://play.google.com/store/apps/details?id=ai.rialty.app;end';
    }
  };

  // ── MFA challenge state ──────────────────────────────────────────────────────
  // 'login'        — normal email/password form
  // 'otp'          — MFA OTP entry (after auth/multi-factor-auth-required)
  // 'trust-prompt' — offer to trust this browser after successful MFA
  const [mfaStep, setMfaStep] = useState('login');
  const [mfaResolver, setMfaResolver] = useState(null);
  const [mfaVerificationId, setMfaVerificationId] = useState(null);
  const [mfaOtpDigits, setMfaOtpDigits] = useState(['', '', '', '', '', '']);
  const [mfaPhoneHint, setMfaPhoneHint] = useState('');
  const [mfaResendTimer, setMfaResendTimer] = useState(0);
  const mfaOtpRefs = useRef([]);
  const mfaRecaptchaRef = useRef(null);
  const pendingMfaCredential = useRef(null);

  // Resend countdown
  useEffect(() => {
    if (mfaResendTimer <= 0) return;
    const t = setInterval(() => setMfaResendTimer(p => p - 1), 1000);
    return () => clearInterval(t);
  }, [mfaResendTimer]);

  // Cleanup reCAPTCHA verifier on unmount
  useEffect(() => {
    return () => {
      if (mfaRecaptchaRef.current) {
        mfaRecaptchaRef.current.clear();
        mfaRecaptchaRef.current = null;
      }
    };
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // ── Post-login finalization ──────────────────────────────────────────────────
  // Shared by normal email login, trusted-device shortcut, and MFA challenge paths.
  const completeLogin = async (userCredential, emailAddress) => {
    const firebaseToken = await userCredential.user.getIdToken();
    const firebaseUid = userCredential.user.uid;

    setStatus({ type: 'info', message: 'Verifying account...' });

    console.log('Calling backend API with:', {
      url: API_ENDPOINTS.userByFirebaseUid(firebaseUid, true),
      firebaseUid,
      hasToken: !!firebaseToken,
    });

    const fetchBackendUser = async () => {
      const response = await apiClient.get(
        API_ENDPOINTS.userByFirebaseUid(firebaseUid, true),
        { headers: { 'Authorization': `Bearer ${firebaseToken}`, 'Content-Type': 'application/json' } }
      );
      const resData = response.data;
      if (resData?.data && typeof resData.data === 'object' && !Array.isArray(resData.data)) return resData.data;
      if (resData?.content && typeof resData.content === 'object' && !Array.isArray(resData.content)) return resData.content;
      return resData;
    };

    let backendData = null;
    try {
      console.log('Attempting backend verification...');
      backendData = await fetchBackendUser();
      console.log('Backend verification successful:', backendData);
    } catch (apiError) {
      console.error('Backend verification failed:', {
        message: apiError.message,
        code: apiError.code,
        response: apiError.response?.data,
        status: apiError.response?.status,
      });
      const st = apiError.response?.status;
      const serverMessage = apiError.response?.data?.message;
      if (st === 401) {
        setStatus({ type: 'error', message: 'Unauthorized. Please check your credentials.' });
      } else if (st === 404) {
        setStatus({ type: 'error', message: 'Account not found. Please register first.' });
      } else if (serverMessage) {
        setStatus({ type: 'error', message: serverMessage });
      } else {
        setStatus({ type: 'error', message: 'Server error. Please try again.' });
      }
      setIsSubmitting(false);
      return;
    }

    setStatus({ type: 'success', message: 'Sign In Successful!' });

    const rawActiveRole = backendData?.activeRole ||
      (Array.isArray(backendData?.roles) && backendData.roles.length > 0 ? backendData.roles[0] : null) ||
      backendData?.role || '';
    const normalizedActiveRole = rawActiveRole
      ? rawActiveRole.replace(/^ROLE_/i, '').toUpperCase() : '';

    const userData = {
      email: emailAddress,
      firebaseUid,
      isAuthenticated: true,
      loginTime: new Date().toISOString(),
      ...(backendData || {}),
      ...(normalizedActiveRole ? { role: normalizedActiveRole } : {}),
    };

    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('isAuthenticated', 'true');
    localStorage.setItem('firebaseToken', firebaseToken);
    if (normalizedActiveRole) localStorage.setItem('currentRole', normalizedActiveRole);

    loadProspectStages('CA');

    apiClient.post(API_ENDPOINTS.loginCompleted, {}, {
      headers: { 'Authorization': `Bearer ${firebaseToken}` },
    }).catch(err => console.warn('[Auth] login-completed notification failed:', err?.message));

    // Fire-and-forget FCM web push registration. All failure modes
    // (permission denied, missing VAPID, backend down) are handled
    // inside initWebPush — nothing here needs to await or catch.
    initWebPush(firebaseToken);

    const pendingInviteRoleEmail = sessionStorage.getItem('pendingInviteRole');
    if (pendingInviteRoleEmail) {
      const roleUserId = userData.id || userData.firebaseUid || '';
      if (roleUserId) {
        try {
          await apiClient.post(
            API_ENDPOINTS.userRoles(roleUserId),
            { role: pendingInviteRoleEmail },
            { headers: { 'Authorization': `Bearer ${firebaseToken}` } }
          );
          console.log('Added pending role:', pendingInviteRoleEmail);
        } catch (err) {
          console.error('Error adding pending role after login:', err);
          // Server enforces role-class exclusivity — a 400 here means the
          // invited role conflicts with the account's existing role class
          // (SERVICE_PROVIDER vs main-app). Surface to the user before they
          // land on a dashboard that won't reflect the "accepted" role.
          if (err?.response?.status === 400) {
            const serverMsg = err?.response?.data?.message
              || err?.response?.data?.error
              || 'The invited role cannot be added to your account.';
            sessionStorage.removeItem('pendingInviteRole');
            // eslint-disable-next-line no-alert
            window.alert(serverMsg);
          }
        }
      }
      sessionStorage.removeItem('pendingInviteRole');
    }

    const pendingAgentVerification = sessionStorage.getItem('agentVerificationData');
    const pendingBrokerVerification = sessionStorage.getItem('brokerVerificationData');
    if (pendingAgentVerification || pendingBrokerVerification) {
      try {
        const verifyData = JSON.parse(pendingAgentVerification || pendingBrokerVerification);
        const userId = userData.id || userData.firebaseUid || '';
        if (userId) {
          if (pendingAgentVerification) {
            await apiClient.put(
              API_ENDPOINTS.agentProfile(userId),
              {
                licenseState: verifyData.stateCode,
                licenseNumber: verifyData.licenseNumber,
                narId: verifyData.narId || '',
                brokerageName: verifyData.brokerageName || '',
              },
              { headers: { 'Authorization': `Bearer ${firebaseToken}` } }
            );
          } else {
            await apiClient.put(
              API_ENDPOINTS.brokerProfile(userId),
              {
                companyName: verifyData.companyName || verifyData.brokerageName || '',
                companyLicenseNumber: verifyData.companyLicenseNumber || verifyData.brokerLicenseNumber || '',
                companyLicenseState: verifyData.companyLicenseState || verifyData.stateCode || '',
                companyAddress: verifyData.companyAddress || verifyData.brokerageAddress || '',
                companyPhone: verifyData.companyPhone || '',
              },
              { headers: { 'Authorization': `Bearer ${firebaseToken}` } }
            );
          }
        }
      } catch (err) { console.error('Error applying verification data after login:', err); }
      finally {
        sessionStorage.removeItem('agentVerificationData');
        sessionStorage.removeItem('brokerVerificationData');
      }
    }

    const pendingInvite = sessionStorage.getItem('inviteData');
    if (pendingInvite) {
      try {
        const inviteInfo = JSON.parse(pendingInvite);
        const userId = userData.id || userData.odooId || userData.firebaseUid || '';
        const userName = [userData.firstName, userData.lastName].filter(Boolean).join(' ') || userData.fullName || userData.name || '';
        const authHeader = { 'Authorization': `Bearer ${firebaseToken}`, 'Content-Type': 'application/json' };

        if (inviteInfo.key && inviteInfo.type === 'transaction') {
          try {
            const acceptRes = await apiClient.post(
              API_ENDPOINTS.acceptInvite,
              { invitationKey: inviteInfo.key, userId, userName },
              { headers: authHeader }
            );
            sessionStorage.removeItem('inviteData');
            const txnId = acceptRes.data?.transactionId || acceptRes.data?.data?.transactionId;
            setTimeout(() => { navigate(txnId ? `/transaction/${txnId}` : getPostLoginHome(), { replace: true }); }, 1000);
            return;
          } catch (acceptErr) {
            console.error('Error accepting transaction invite after login:', acceptErr);
            sessionStorage.removeItem('inviteData');
          }
        } else if (inviteInfo.key && inviteInfo.type === 'user') {
          try {
            const acceptRes = await apiClient.post(
              API_ENDPOINTS.acceptUserInvite(inviteInfo.key), {}, { headers: authHeader }
            );
            sessionStorage.removeItem('inviteData');
            try {
              const profileRes = await apiClient.get(API_ENDPOINTS.userMe, { headers: authHeader });
              const refreshed = profileRes.data?.data || profileRes.data?.content || profileRes.data;
              if (refreshed && typeof refreshed === 'object') {
                localStorage.setItem('user', JSON.stringify({ ...userData, ...refreshed }));
              }
            } catch { /* non-critical */ }
            const txnId = acceptRes.data?.data?.transactionId || acceptRes.data?.transactionId;
            setTimeout(() => { navigate(txnId ? `/transaction/${txnId}` : getPostLoginHome(), { replace: true }); }, 1000);
            return;
          } catch (acceptErr) {
            console.error('Error accepting user invite after login:', acceptErr);
            sessionStorage.removeItem('inviteData');
          }
        } else if (!inviteInfo.key && inviteInfo.transactionId && inviteInfo.role) {
          try {
            await apiClient.post(
              API_ENDPOINTS.transactionClients(inviteInfo.transactionId),
              { userId, role: inviteInfo.role.toUpperCase() },
              { headers: authHeader }
            );
            sessionStorage.removeItem('inviteData');
            setTimeout(() => { navigate(`/transaction/${inviteInfo.transactionId}`, { replace: true }); }, 1000);
            return;
          } catch (acceptErr) {
            console.error('Error adding user to transaction after login:', acceptErr);
            sessionStorage.removeItem('inviteData');
          }
        }
      } catch (e) { console.error('Error parsing invite data:', e); }
    }

    // Post-login passkey enrollment prompt — only when the device can do
    // biometrics AND this browser hasn't already enrolled AND the user
    // hasn't previously said "don't ask again" (by leaving the "Remind me
    // next time" checkbox off when they tapped Not now). Skips silently on
    // non-biometric devices or when a passkey already exists. Passkey login
    // (customToken path) doesn't hit this — that path already implies
    // enrollment.
    let shouldPromptEnroll = false;
    try {
      if (isWebAuthnSupported() && !hasEnrolledPasskey() && !hasDismissedPasskeyPrompt()) {
        const hasBiometric = await isPlatformAuthenticatorAvailable();
        shouldPromptEnroll = !!hasBiometric;
      }
    } catch { /* fail-open, skip prompt */ }

    if (shouldPromptEnroll) {
      // Defer the dashboard navigation until the user answers the prompt.
      setEnrollPrompt({ navigateTo: getPostLoginHome() });
      return;
    }

    setTimeout(() => { navigate(getPostLoginHome()); }, 1000);
  };

  // Get a fresh Firebase ID token for the enrollPasskey ceremony. Same
  // shape as the WebAuthn service's other callers (Settings passkey list).
  const getFreshFirebaseToken = async () => {
    try { return await auth.currentUser?.getIdToken(true); }
    catch { return null; }
  };

  const handleEnrollAccept = async () => {
    setEnrolling(true);
    setEnrollError('');
    try {
      await enrollPasskey(getFreshFirebaseToken);
      const dest = enrollPrompt?.navigateTo || getPostLoginHome();
      setEnrollPrompt(null);
      setEnrolling(false);
      setTimeout(() => { navigate(dest); }, 300);
    } catch (err) {
      // Common cases: user cancelled the fingerprint prompt (NotAllowedError),
      // device denied, or the server rejected the attestation. Let the user
      // retry or skip — don't strand them at the login screen.
      const cancelled = err?.name === 'NotAllowedError' || err?.name === 'AbortError';
      // InvalidStateError = "The authenticator was previously registered".
      // The server included this authenticator's credentialId in
      // excludeCredentials during the options call because it's already
      // enrolled server-side for this user; the browser refused to
      // re-create the same credential. Local flag was likely cleared
      // during an earlier failed login (e.g. before the signBlob backend
      // fix). Treat this as an implicit success — the passkey IS enrolled
      // for this account on this device, so set the local flag and
      // continue to the dashboard as if enrollment just succeeded.
      const alreadyRegistered = err?.name === 'InvalidStateError'
        || /previously registered|already registered/i.test(err?.message || '');
      if (alreadyRegistered) {
        try { localStorage.setItem(WEB_PASSKEY_FLAG, 'true'); } catch { /* ignore */ }
        const dest = enrollPrompt?.navigateTo || getPostLoginHome();
        setEnrollPrompt(null);
        setEnrolling(false);
        setTimeout(() => { navigate(dest); }, 200);
        return;
      }
      setEnrolling(false);
      setEnrollError(
        cancelled
          ? 'Fingerprint setup was cancelled. You can try again or skip.'
          : (err?.response?.data?.message || err?.message || 'Could not set up fingerprint. Please try again.'),
      );
    }
  };

  const handleEnrollSkip = () => {
    const dest = enrollPrompt?.navigateTo || getPostLoginHome();
    // If the "Don't ask again" box was ticked, persist dismissal so this
    // prompt stops firing on every login on this browser. Default (unticked)
    // = the prompt reappears next login.
    if (enrollDontAsk) {
      dismissPasskeyPrompt();
    }
    setEnrollPrompt(null);
    setEnrollError('');
    setEnrollDontAsk(false);
    setTimeout(() => { navigate(dest); }, 200);
  };

  // ── Trusted device login shortcut ────────────────────────────────────────────
  // Only attempted when this browser was previously registered as trusted
  // (webDeviceTrusted flag is set after a successful trust registration).
  // Mirrors mobile: only calls this endpoint when biometric credentials exist,
  // i.e. the app already knows the device was previously trusted.
  const tryTrustedDeviceLogin = async (email) => {
    const webDeviceId = localStorage.getItem('webDeviceId');
    const isTrusted = localStorage.getItem('webDeviceTrusted') === 'true';
    if (!webDeviceId || !isTrusted) return null;
    try {
      const res = await apiClient.post(API_ENDPOINTS.trustedDeviceLogin, { email, deviceId: webDeviceId });
      return res.data?.customToken || null;
    } catch { return null; }
  };

  // ── Sign in with Fingerprint (WebAuthn / passkey) ─────────────────────────────
  // Server-verified: the assertion is checked against the stored public key, then a
  // Firebase custom token is returned and exchanged — OTP is skipped entirely.
  const handlePasskeyLogin = async () => {
    if (!formData.email.trim()) {
      setStatus({ type: 'error', message: 'Please enter your email first, then use your fingerprint.' });
      return;
    }
    setIsSubmitting(true);
    setStatus({ type: 'info', message: 'Waiting for your fingerprint…' });
    try {
      const customToken = await loginWithPasskey(formData.email.trim());
      if (!customToken) {
        // Service already cleared the WEB_PASSKEY_FLAG when the server
        // reports zero allowCredentials for this email. Refresh
        // showPasskey so the button disappears immediately — the user
        // signs in with password now and can re-enroll via the
        // post-login prompt or Settings.
        setShowPasskey(false);
        setStatus({ type: 'error', message: 'No passkey found for this account. Please sign in with your password, then set up fingerprint in Settings.' });
        setIsSubmitting(false);
        return;
      }
      const userCredential = await signInWithCustomToken(auth, customToken);
      console.log('Firebase authentication successful (passkey)');
      await completeLogin(userCredential, formData.email.trim());
    } catch (err) {
      // NotAllowedError = user cancelled or the prompt timed out.
      const cancelled = err?.name === 'NotAllowedError' || err?.name === 'AbortError';
      console.warn('[WebAuthn] passkey login failed:', err?.name, err?.message);
      if (!cancelled) {
        // Non-cancel failure — the local authenticator couldn't produce
        // a valid assertion (passkey deleted from the OS keyring, server
        // rejected, etc.). Drop the stale WEB_PASSKEY_FLAG and hide the
        // button so the user isn't left staring at a control that keeps
        // failing on every click.
        try { localStorage.removeItem(WEB_PASSKEY_FLAG); } catch { /* ignore */ }
        setShowPasskey(false);
      }
      setStatus({
        type: 'error',
        message: cancelled
          ? 'Fingerprint sign-in was cancelled.'
          : 'Fingerprint sign-in failed. Please sign in with your password.',
      });
      setIsSubmitting(false);
    }
  };

  // ── MFA challenge: send OTP ──────────────────────────────────────────────────
  const sendMfaChallengeOtp = async (resolver) => {
    try {
      setStatus({ type: 'info', message: 'Sending verification code — if a challenge appears on screen, please complete it to continue.' });

      if (mfaRecaptchaRef.current) {
        mfaRecaptchaRef.current.clear();
        mfaRecaptchaRef.current = null;
      }
      mfaRecaptchaRef.current = new RecaptchaVerifier(auth, 'mfa-recaptcha-container', { size: 'invisible' });

      const phoneHint = resolver.hints.find(h => h.factorId === PhoneMultiFactorGenerator.FACTOR_ID) || resolver.hints[0];
      const phoneAuthProvider = new PhoneAuthProvider(auth);

      const verificationId = await Promise.race([
        phoneAuthProvider.verifyPhoneNumber(
          { multiFactorHint: phoneHint, session: resolver.session },
          mfaRecaptchaRef.current,
        ),
        new Promise((_, reject) => setTimeout(
          () => reject(Object.assign(new Error('reCAPTCHA timed out'), { code: 'auth/recaptcha-timeout' })),
          MFA_RECAPTCHA_TIMEOUT_MS,
        )),
      ]);

      setMfaVerificationId(verificationId);

      setMfaResolver(resolver);
      setMfaPhoneHint(phoneHint?.phoneNumber || 'your registered phone');
      setMfaOtpDigits(['', '', '', '', '', '']);
      setMfaResendTimer(MFA_RESEND_TIMER_SECONDS);
      setMfaStep('otp');
      setStatus({ type: '', message: '' });
      setTimeout(() => mfaOtpRefs.current[0]?.focus(), 100);
    } catch (err) {
      if (mfaRecaptchaRef.current) {
        mfaRecaptchaRef.current.clear();
        mfaRecaptchaRef.current = null;
      }
      removeStrayMfaRecaptcha();
      console.error('[MFA] sendMfaChallengeOtp error:', err.code, err.message);
      // The rialty-ai-dev Firebase project rejects every reCAPTCHA token issued
      // on localhost (proven: same call passes on dev.rialty.ai), so phone-MFA
      // login can never succeed locally for a real number — explain that
      // instead of surfacing the raw "auth/invalid-app-credential".
      const isLocalhost = ['localhost', '127.0.0.1'].includes(window.location.hostname);
      if (err.code === 'auth/invalid-app-credential' && isLocalhost) {
        setStatus({
          type: 'error',
          message: 'Phone verification can\'t run on localhost for this account (Firebase rejects localhost reCAPTCHA). Sign in on dev.rialty.ai instead, or use a test account whose MFA phone is a Firebase test number.',
        });
      } else {
        setStatus({ type: 'error', message: err.message || 'Failed to send verification code. Please try again.' });
      }
      setIsSubmitting(false);
    }
  };

  // ── MFA challenge: verify OTP ────────────────────────────────────────────────
  const handleMfaVerify = async () => {
    const code = mfaOtpDigits.join('');
    if (code.length < MFA_OTP_LENGTH) {
      setStatus({ type: 'error', message: 'Please enter all 6 digits.' });
      return;
    }

    setIsSubmitting(true);
    setStatus({ type: '', message: '' });

    try {
      const credential = PhoneAuthProvider.credential(mfaVerificationId, code);
      const assertion = PhoneMultiFactorGenerator.assertion(credential);
      const userCredential = await mfaResolver.resolveSignIn(assertion);

      // Check whether this browser is already trusted (non-blocking)
      const webDeviceId = localStorage.getItem('webDeviceId');
      let isTrusted = false;
      if (webDeviceId) {
        try {
          const token = await userCredential.user.getIdToken();
          const checkRes = await apiClient.post(
            API_ENDPOINTS.trustedDeviceCheck,
            { deviceId: webDeviceId },
            { headers: { 'Authorization': `Bearer ${token}` } },
          );
          isTrusted = !!checkRes.data?.isTrusted;
        } catch { /* non-blocking */ }
      }

      if (isTrusted || !webDeviceId) {
        await completeLogin(userCredential, formData.email);
      } else {
        pendingMfaCredential.current = userCredential;
        setMfaStep('trust-prompt');
        setIsSubmitting(false);
      }
    } catch (err) {
      console.error('[MFA] handleMfaVerify error:', err);
      if (err.code === 'auth/invalid-verification-code') {
        setStatus({ type: 'error', message: 'Invalid verification code. Please try again.' });
      } else if (err.code === 'auth/code-expired') {
        setStatus({ type: 'error', message: 'Code expired. Please request a new one.' });
      } else {
        setStatus({ type: 'error', message: err.message || 'Verification failed. Please try again.' });
      }
      setIsSubmitting(false);
    }
  };

  // ── MFA OTP input handlers ───────────────────────────────────────────────────
  const handleMfaOtpInput = (index, value) => {
    if (value.length > 1) {
      const digits = value.replace(/\D/g, '').slice(0, MFA_OTP_LENGTH).split('');
      const newOtp = [...mfaOtpDigits];
      digits.forEach((d, i) => { if (index + i < MFA_OTP_LENGTH) newOtp[index + i] = d; });
      setMfaOtpDigits(newOtp);
      setTimeout(() => mfaOtpRefs.current[Math.min(index + digits.length, MFA_OTP_LENGTH - 1)]?.focus(), 0);
      if (newOtp.join('').length === MFA_OTP_LENGTH) setTimeout(handleMfaVerify, 0);
      return;
    }
    const digit = value.replace(/\D/g, '').slice(-1);
    const newOtp = [...mfaOtpDigits];
    newOtp[index] = digit;
    setMfaOtpDigits(newOtp);
    if (digit && index < MFA_OTP_LENGTH - 1) setTimeout(() => mfaOtpRefs.current[index + 1]?.focus(), 0);
  };

  const handleMfaOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !mfaOtpDigits[index] && index > 0) {
      setTimeout(() => mfaOtpRefs.current[index - 1]?.focus(), 0);
    }
  };

  // ── MFA challenge: resend OTP ────────────────────────────────────────────────
  const handleMfaResend = async () => {
    if (mfaResendTimer > 0 || !mfaResolver) return;
    setIsSubmitting(true);
    await sendMfaChallengeOtp(mfaResolver);
    setIsSubmitting(false);
  };

  // ── MFA challenge: trust / skip trust ───────────────────────────────────────
  const handleMfaTrustDevice = async () => {
    setIsSubmitting(true);
    const webDeviceId = localStorage.getItem('webDeviceId');
    if (webDeviceId) {
      try {
        const token = await pendingMfaCredential.current.user.getIdToken();
        await apiClient.post(
          API_ENDPOINTS.trustedDeviceTrust,
          { deviceId: webDeviceId, deviceName: getWebDeviceName(), platform: 'web' },
          { headers: { 'Authorization': `Bearer ${token}` } },
        );
        localStorage.setItem('webDeviceTrusted', 'true');
      } catch (err) { console.warn('[MFA] Trust device failed (non-blocking):', err.message); }
    }
    await completeLogin(pendingMfaCredential.current, formData.email);
  };

  const handleMfaSkipTrust = async () => {
    setIsSubmitting(true);
    await completeLogin(pendingMfaCredential.current, formData.email);
  };

  // ── Email / Password submit ──────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: '', message: '' });

    if (!formData.email.trim()) {
      setStatus({ type: 'error', message: 'Please enter your email' });
      setIsSubmitting(false);
      return;
    }
    if (!formData.password) {
      setStatus({ type: 'error', message: 'Please enter your password' });
      setIsSubmitting(false);
      return;
    }

    try {
      setStatus({ type: 'info', message: 'Signing in...' });

      // Try trusted device shortcut first — if this browser is trusted the backend
      // returns a custom Firebase token and MFA is skipped entirely.
      const customToken = await tryTrustedDeviceLogin(formData.email);
      if (customToken) {
        const userCredential = await signInWithCustomToken(auth, customToken);
        console.log('Firebase authentication successful (trusted device)');
        await completeLogin(userCredential, formData.email);
        return;
      }

      // Normal Firebase sign-in
      const userCredential = await signInWithEmailAndPassword(auth, formData.email, formData.password);
      console.log('Firebase authentication successful');
      await completeLogin(userCredential, formData.email);

    } catch (error) {
      console.error('Login error:', error);
      console.error('Error details:', { message: error.message, code: error.code, name: error.name, stack: error.stack });

      if (error.code === 'auth/multi-factor-auth-required') {
        // MFA is enforced on this account — extract the resolver and send the OTP.
        const resolver = getMultiFactorResolver(auth, error);
        await sendMfaChallengeOtp(resolver);
        // sendMfaChallengeOtp calls setIsSubmitting(false) on error; on success the
        // view transitions to 'otp' and the button becomes the Verify button.
        setIsSubmitting(false);
        return;
      }

      if (error.code?.startsWith('auth/')) {
        switch (error.code) {
          case 'auth/invalid-credential':
          case 'auth/user-not-found':
          case 'auth/wrong-password':
            setStatus({ type: 'error', message: 'Invalid email or password. Please try again.' });
            break;
          case 'auth/invalid-email':
            setStatus({ type: 'error', message: 'Invalid email address format.' });
            break;
          case 'auth/user-disabled':
            setStatus({ type: 'error', message: 'This account has been disabled. Please contact support.' });
            break;
          case 'auth/network-request-failed':
            setStatus({ type: 'error', message: 'Network error. Please check your internet connection.' });
            break;
          case 'auth/too-many-requests':
            setStatus({ type: 'error', message: 'Too many failed attempts. Please try again later.' });
            break;
          default:
            setStatus({ type: 'error', message: error.message || 'Login failed. Please try again.' });
        }
      } else if (error.response) {
        console.error('API Response Error:', error.response.data);
        setStatus({ type: 'error', message: error.response.data?.message || 'Server error. Please try again.' });
      } else if (error.request) {
        console.error('Network Error - No response received:', error.request);
        setStatus({ type: 'error', message: 'Cannot connect to server. Please check your internet connection.' });
      } else {
        setStatus({ type: 'error', message: error.message || 'Login failed. Please check your credentials.' });
      }
      setIsSubmitting(false);
    }
  };

  // ── Google Sign-In (unchanged) ───────────────────────────────────────────────
  const handleGoogleSignIn = async () => {
    setIsSubmitting(true);
    setStatus({ type: '', message: '' });

    try {
      setStatus({ type: 'info', message: 'Connecting to Google...' });
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);

      const firebaseToken = await result.user.getIdToken();
      const firebaseUid = result.user.uid;
      const userEmail = result.user.email;
      const userName = result.user.displayName;

      console.log('Google authentication successful');
      console.log('User email:', userEmail);
      console.log('User name:', userName);

      setStatus({ type: 'info', message: 'Verifying account...' });

      console.log('Calling backend API with:', {
        url: API_ENDPOINTS.userByFirebaseUid(firebaseUid, true),
        firebaseUid: firebaseUid,
        hasToken: !!firebaseToken
      });

      const fetchGoogleBackendUser = async () => {
        const response = await apiClient.get(
          API_ENDPOINTS.userByFirebaseUid(firebaseUid, true),
          {
            headers: {
              'Authorization': `Bearer ${firebaseToken}`,
              'Content-Type': 'application/json'
            },
          }
        );
        const resData = response.data;
        if (resData?.data && typeof resData.data === 'object' && !Array.isArray(resData.data)) {
          return resData.data;
        }
        if (resData?.content && typeof resData.content === 'object' && !Array.isArray(resData.content)) {
          return resData.content;
        }
        return resData;
      };

      const registerGoogleUser = async () => {
        const nameParts = (userName || userEmail.split('@')[0]).split(' ');
        const regFirstName = nameParts[0] || '';
        const regLastName = nameParts.length > 1 ? nameParts[nameParts.length - 1] : '';
        const regMiddleName = nameParts.length > 2 ? nameParts.slice(1, -1).join(' ') : null;
        const registrationData = {
          firstName: regFirstName,
          middleName: regMiddleName || null,
          lastName: regLastName,
          email: userEmail,
          firebaseUid: firebaseUid,
          role: null,
          phone: null,
          address: null,
          state: null,
          city: null,
          postalCode: null,
          licenseState: null,
          agentNumber: null,
          narId: null,
          brokerageName: null,
          brokerOfRecordName: null,
          brokerLicenseNumber: null,
          brokerageAddress: null,
          brokerageNarId: null
        };

        const registerResponse = await apiClient.post(
          API_ENDPOINTS.users,
          registrationData,
          {
            headers: {
              'Authorization': `Bearer ${firebaseToken}`,
              'Content-Type': 'application/json'
            },
          }
        );
        const resData = registerResponse.data;
        if (resData?.data && typeof resData.data === 'object' && !Array.isArray(resData.data)) {
          return resData.data;
        }
        if (resData?.content && typeof resData.content === 'object' && !Array.isArray(resData.content)) {
          return resData.content;
        }
        return resData;
      };

      const attemptGoogleBackendVerification = async () => {
        try {
          return await fetchGoogleBackendUser();
        } catch (apiError) {
          if (apiError.response?.status === 404) {
            console.log('User not found in backend, registering...');
            return await registerGoogleUser();
          }
          throw apiError;
        }
      };

      let backendData = null;
      try {
        console.log('Attempting Google backend verification...');
        backendData = await attemptGoogleBackendVerification();
        console.log('Backend verification successful:', backendData);
      } catch (apiError) {
        console.error('Google backend verification failed:', {
          message: apiError.message,
          code: apiError.code,
          response: apiError.response?.data,
          status: apiError.response?.status
        });
        const status = apiError.response?.status;
        const serverMessage = apiError.response?.data?.message;
        if (status === 401) {
          setStatus({ type: 'error', message: 'Unauthorized. Please try again.' });
        } else if (serverMessage) {
          setStatus({ type: 'error', message: serverMessage });
        } else {
          setStatus({ type: 'error', message: 'Server error. Please try again.' });
        }
        setIsSubmitting(false);
        return;
      }

      setStatus({ type: 'success', message: 'Sign In Successful!' });

      const rawActiveRoleGoogle = backendData?.activeRole ||
        (Array.isArray(backendData?.roles) && backendData.roles.length > 0 ? backendData.roles[0] : null) ||
        backendData?.role || '';
      const normalizedActiveRoleGoogle = rawActiveRoleGoogle
        ? rawActiveRoleGoogle.replace(/^ROLE_/i, '').toUpperCase()
        : '';

      const userData = {
        email: userEmail,
        fullName: userName,
        firebaseUid: firebaseUid,
        isAuthenticated: true,
        loginTime: new Date().toISOString(),
        loginMethod: 'google',
        ...(backendData || {}),
        ...(normalizedActiveRoleGoogle ? { role: normalizedActiveRoleGoogle } : {})
      };

      localStorage.setItem('user', JSON.stringify(userData));
      localStorage.setItem('isAuthenticated', 'true');
      localStorage.setItem('firebaseToken', firebaseToken);
      if (normalizedActiveRoleGoogle) {
        localStorage.setItem('currentRole', normalizedActiveRoleGoogle);
      }

      loadProspectStages('CA');

      apiClient.post(API_ENDPOINTS.loginCompleted, {}, {
        headers: { 'Authorization': `Bearer ${firebaseToken}` }
      }).catch(err => console.warn('[Auth] login-completed notification failed:', err?.message));

      const pendingInviteRoleGoogle = sessionStorage.getItem('pendingInviteRole');
      if (pendingInviteRoleGoogle) {
        const roleUserIdGoogle = userData.id || userData.firebaseUid || '';
        if (roleUserIdGoogle) {
          try {
            await apiClient.post(
              API_ENDPOINTS.userRoles(roleUserIdGoogle),
              { role: pendingInviteRoleGoogle },
              { headers: { 'Authorization': `Bearer ${firebaseToken}` } }
            );
            console.log('Added pending role (Google):', pendingInviteRoleGoogle);
          } catch (err) {
            console.error('Error adding pending role after Google login:', err);
          }
        }
        sessionStorage.removeItem('pendingInviteRole');
      }

      const pendingAgentVerificationGoogle = sessionStorage.getItem('agentVerificationData');
      const pendingBrokerVerificationGoogle = sessionStorage.getItem('brokerVerificationData');
      if (pendingAgentVerificationGoogle || pendingBrokerVerificationGoogle) {
        try {
          const verifyData = JSON.parse(pendingAgentVerificationGoogle || pendingBrokerVerificationGoogle);
          const userId = userData.id || userData.firebaseUid || '';
          if (userId) {
            if (pendingAgentVerificationGoogle) {
              await apiClient.post(
                API_ENDPOINTS.agentProfile(userId),
                {
                  licenseState: verifyData.stateCode,
                  licenseNumber: verifyData.licenseNumber,
                  narId: verifyData.narId || '',
                  brokerageName: verifyData.brokerageName || '',
                },
                { headers: { 'Authorization': `Bearer ${firebaseToken}` } }
              );
            } else {
              await apiClient.post(
                API_ENDPOINTS.brokerProfile(userId),
                {
                  licenseState: verifyData.stateCode,
                  brokerLicenseNumber: verifyData.brokerLicenseNumber,
                  brokerageName: verifyData.brokerageName || '',
                  brokerageAddress: verifyData.brokerageAddress || '',
                },
                { headers: { 'Authorization': `Bearer ${firebaseToken}` } }
              );
            }
          }
        } catch (err) {
          console.error('Error applying verification data after Google login:', err);
        } finally {
          sessionStorage.removeItem('agentVerificationData');
          sessionStorage.removeItem('brokerVerificationData');
        }
      }

      const pendingInviteGoogle = sessionStorage.getItem('inviteData');
      if (pendingInviteGoogle) {
        try {
          const inviteInfo = JSON.parse(pendingInviteGoogle);
          const userId = userData.id || userData.odooId || userData.firebaseUid || '';
          const userNameG = [userData.firstName, userData.lastName].filter(Boolean).join(' ') || userData.fullName || userData.name || '';
          const authHeaderG = { 'Authorization': `Bearer ${firebaseToken}`, 'Content-Type': 'application/json' };

          if (inviteInfo.key && inviteInfo.type === 'transaction') {
            try {
              const acceptRes = await apiClient.post(
                API_ENDPOINTS.acceptInvite,
                { invitationKey: inviteInfo.key, userId, userName: userNameG },
                { headers: authHeaderG }
              );
              sessionStorage.removeItem('inviteData');
              const txnId = acceptRes.data?.transactionId || acceptRes.data?.data?.transactionId;
              setTimeout(() => {
                navigate(txnId ? `/transaction/${txnId}` : getPostLoginHome(), { replace: true });
              }, 1000);
              return;
            } catch (acceptErr) {
              console.error('Error accepting transaction invite after Google login:', acceptErr);
              sessionStorage.removeItem('inviteData');
            }
          } else if (inviteInfo.key && inviteInfo.type === 'user') {
            try {
              const acceptRes = await apiClient.post(
                API_ENDPOINTS.acceptUserInvite(inviteInfo.key),
                {},
                { headers: authHeaderG }
              );
              sessionStorage.removeItem('inviteData');
              try {
                const profileRes = await apiClient.get(API_ENDPOINTS.userMe, { headers: authHeaderG });
                const refreshed = profileRes.data?.data || profileRes.data?.content || profileRes.data;
                if (refreshed && typeof refreshed === 'object') {
                  localStorage.setItem('user', JSON.stringify({ ...userData, ...refreshed }));
                }
              } catch { /* non-critical */ }
              const txnId = acceptRes.data?.data?.transactionId || acceptRes.data?.transactionId;
              setTimeout(() => {
                navigate(txnId ? `/transaction/${txnId}` : getPostLoginHome(), { replace: true });
              }, 1000);
              return;
            } catch (acceptErr) {
              console.error('Error accepting user invite after Google login:', acceptErr);
              sessionStorage.removeItem('inviteData');
            }
          } else if (!inviteInfo.key && inviteInfo.transactionId && inviteInfo.role) {
            try {
              await apiClient.post(
                API_ENDPOINTS.transactionClients(inviteInfo.transactionId),
                { userId, role: inviteInfo.role.toUpperCase() },
                { headers: authHeaderG }
              );
              sessionStorage.removeItem('inviteData');
              setTimeout(() => {
                navigate(`/transaction/${inviteInfo.transactionId}`, { replace: true });
              }, 1000);
              return;
            } catch (acceptErr) {
              console.error('Error adding user to transaction after Google login:', acceptErr);
              sessionStorage.removeItem('inviteData');
            }
          }
        } catch (e) {
          console.error('Error parsing invite data:', e);
        }
      }

      setTimeout(() => { navigate(getPostLoginHome()); }, 1000);

    } catch (error) {
      console.error('Google Sign-In error:', error);
      console.error('Error details:', { message: error.message, code: error.code, name: error.name });

      if (error.code) {
        switch (error.code) {
          case 'auth/operation-not-allowed':
            setStatus({ type: 'error', message: 'Google Sign-In is not enabled. Please contact administrator.' });
            console.error('FIREBASE SETUP REQUIRED: Enable Google Sign-In in Firebase Console > Authentication > Sign-in method');
            break;
          case 'auth/popup-closed-by-user':
            setStatus({ type: 'error', message: 'Sign-in popup was closed. Please try again.' });
            break;
          case 'auth/cancelled-popup-request':
            setStatus({ type: 'error', message: 'Sign-in was cancelled. Please try again.' });
            break;
          case 'auth/popup-blocked':
            setStatus({ type: 'error', message: 'Pop-up was blocked by browser. Please allow pop-ups for this site.' });
            break;
          case 'auth/account-exists-with-different-credential':
            setStatus({ type: 'error', message: 'An account already exists with this email using a different sign-in method.' });
            break;
          case 'auth/network-request-failed':
            setStatus({ type: 'error', message: 'Network error. Please check your internet connection.' });
            break;
          case 'auth/unauthorized-domain':
            setStatus({ type: 'error', message: 'This domain is not authorized for Google Sign-In. Please contact the administrator.' });
            console.error('FIREBASE SETUP REQUIRED: Add this domain to Firebase Console > Authentication > Settings > Authorized domains');
            break;
          case 'auth/internal-error':
            setStatus({ type: 'error', message: 'Google Sign-In configuration error. Please contact the administrator.' });
            break;
          default:
            setStatus({ type: 'error', message: error.message || 'Google sign-in failed. Please try again.' });
        }
      } else {
        setStatus({ type: 'error', message: 'Google sign-in failed. Please try again.' });
      }

      setIsSubmitting(false);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="login-page" style={showAppBanner ? { paddingTop: '138px' } : {}}>
      {/* Mobile App Banner */}
      {showAppBanner && (
        <div className="app-banner">
          <button className="app-banner-close-btn" onClick={dismissBanner} aria-label="Dismiss">✕</button>
          <img src="/android-chrome-192x192.png" alt="Rialty" className="app-banner-icon" />
          <div className="app-banner-text">
            <span className="app-banner-title">Rialty App</span>
            <span className="app-banner-subtitle">Use the Rialty app for best experience</span>
          </div>
          <button className="app-banner-open-btn" onClick={openApp}>Get</button>
        </div>
      )}

      <main className="login-main-content" style={showAppBanner ? { paddingTop: '64px' } : {}}>
        <div className="signin-container">

          {/* ── Normal login form ── */}
          {mfaStep === 'login' && (
            <>
              <h1 className="signin-title">Sign In</h1>

              {verificationComplete && (
                <div className="status-message-login success" style={{ marginBottom: '16px' }}>
                  Verification complete! Sign in to continue.
                </div>
              )}

              {pendingInviteAccept && !verificationComplete && (
                <div className="status-message-login info" style={{ marginBottom: '16px', background: 'rgba(91,71,214,0.12)', color: '#2D1B69', border: '1px solid rgba(91,71,214,0.25)', borderRadius: '8px', padding: '12px 16px', fontSize: '14px' }}>
                  Sign in to accept your invitation.
                </div>
              )}

              <form onSubmit={handleSubmit} className="email-section">
                <div className="input-wrapper-login">
                  <input
                    type="email"
                    name="email"
                    className="email-input"
                    placeholder="Email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                  <span className="input-icon-left">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                      <circle cx="12" cy="7" r="4" />
                    </svg>
                  </span>
                </div>

                <div className="input-wrapper-login">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    className="email-input email-input-password"
                    placeholder="Password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                  />
                  <span className="input-icon-left">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                    </svg>
                  </span>
                  <button
                    type="button"
                    className="password-toggle-btn"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex={-1}
                  >
                    {showPassword ? (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    ) : (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                        <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                        <line x1="1" y1="1" x2="23" y2="23" />
                      </svg>
                    )}
                  </button>
                </div>

                {status.message && (
                  <div className={`status-message-login ${status.type}`}>
                    {status.message}
                  </div>
                )}

                <button type="submit" className="next-btn" disabled={isSubmitting}>
                  {isSubmitting ? 'Signing in...' : 'Sign In'}
                </button>

                {showPasskey && (
                  <button
                    type="button"
                    className="passkey-btn"
                    onClick={handlePasskeyLogin}
                    disabled={isSubmitting}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 11c-1.66 0-3 1.34-3 3 0 1.53.36 3.06.99 4.4" />
                      <path d="M12 2a10 10 0 0 0-8.66 15" />
                      <path d="M7.5 4.2A8 8 0 0 1 20 11c0 1.5-.2 3-.6 4.4" />
                      <path d="M12 7a4 4 0 0 1 4 4c0 2.5-.3 5-1 7" />
                      <path d="M4.3 15A16 16 0 0 0 6 21" />
                      <path d="M15.5 18.5c.3-1.5.5-3 .5-4.5" />
                    </svg>
                    Sign in with Fingerprint
                  </button>
                )}

                <div className="forgot-link">
                  <Link to="/forgot-password">Forgot your password?</Link>
                </div>

                <div className="forgot-link" style={{ marginTop: '10px' }}>
                  <span style={{ color: '#6B6B7B', fontSize: '14px' }}>Don't have an invite? </span>
                  <Link
                    to="/request-access"
                    style={{ color: '#2D1B69', fontWeight: '600' }}
                    onClick={() => track(EVENTS.CTA_CLICK, { element_label: 'request_access', source_screen: location.pathname })}
                  >
                    Request Access
                  </Link>
                </div>
              </form>
            </>
          )}

          {/* ── MFA OTP challenge ── */}
          {mfaStep === 'otp' && (
            <div className="lp-mfa-section">
              <button
                className="lp-mfa-back-btn"
                onClick={() => { setMfaStep('login'); setStatus({ type: '', message: '' }); }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <polyline points="15 18 9 12 15 6" />
                </svg>
                Back
              </button>

              <div className="lp-mfa-header">
                <div className="lp-mfa-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                  </svg>
                </div>
                <h2 className="lp-mfa-title">Verification Required</h2>
                <p className="lp-mfa-subtitle">
                  We sent a 6-digit code to <strong>{mfaPhoneHint}</strong>. Enter it below.
                </p>
              </div>

              <div className="lp-otp-group">
                {mfaOtpDigits.map((digit, i) => (
                  <input
                    key={i}
                    ref={el => (mfaOtpRefs.current[i] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    className={`lp-otp-box${digit ? ' filled' : ''}`}
                    value={digit}
                    onChange={e => handleMfaOtpInput(i, e.target.value)}
                    onKeyDown={e => handleMfaOtpKeyDown(i, e)}
                    onFocus={e => e.target.select()}
                  />
                ))}
              </div>

              {status.message && (
                <div className={`status-message-login ${status.type}`}>
                  {status.message}
                </div>
              )}

              <button
                className="next-btn"
                onClick={handleMfaVerify}
                disabled={isSubmitting || mfaOtpDigits.join('').length < MFA_OTP_LENGTH}
              >
                {isSubmitting ? 'Verifying...' : 'Verify Code'}
              </button>

              <div className="lp-mfa-resend">
                {mfaResendTimer > 0 ? (
                  <span>Resend code in <strong>{mfaResendTimer}s</strong></span>
                ) : (
                  <button
                    className="lp-mfa-resend-btn"
                    onClick={handleMfaResend}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Sending...' : 'Resend Code'}
                  </button>
                )}
              </div>
            </div>
          )}

          {/* ── Trust this browser prompt ── */}
          {mfaStep === 'trust-prompt' && (
            <div className="lp-mfa-section">
              <div className="lp-trust-success">
                <div className="lp-trust-success-icon">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                    <polyline points="22 4 12 14.01 9 11.01" />
                  </svg>
                </div>
                <p className="lp-trust-success-label">Signed in successfully!</p>
              </div>

              <div className="lp-trust-card">
                <div className="lp-trust-card-header">
                  <div className="lp-trust-card-icon">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="2" y="3" width="20" height="14" rx="2" />
                      <line x1="8" y1="21" x2="16" y2="21" />
                      <line x1="12" y1="17" x2="12" y2="21" />
                    </svg>
                  </div>
                  <span className="lp-trust-card-title">Trust This Browser?</span>
                </div>
                <p className="lp-trust-card-desc">
                  Skip OTP verification on future logins from this browser.
                </p>
                <div className="lp-trust-actions">
                  <button
                    className="lp-trust-skip-btn"
                    onClick={handleMfaSkipTrust}
                    disabled={isSubmitting}
                  >
                    Not Now
                  </button>
                  <button
                    className="next-btn lp-trust-confirm-btn"
                    onClick={handleMfaTrustDevice}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Continuing...' : 'Trust This Browser'}
                  </button>
                </div>
              </div>
            </div>
          )}

        </div>
      </main>

      <footer className="login-footer">
        <div className="footer-links-login">
          <Link to="/privacy">Privacy Policy</Link>
          <span className="footer-separator">|</span>
          <Link to="/terms">Terms of Service</Link>
        </div>
      </footer>

      {/* Required by Firebase RecaptchaVerifier for the invisible reCAPTCHA used
          during the MFA login challenge. Must remain in the DOM and not be hidden
          with display:none — same requirement as the SettingsPage verifier. */}
      <div id="mfa-recaptcha-container" />

      {/* Post-login passkey enrollment prompt. Shown right after a
          successful password sign-in when the device supports biometrics
          and this browser hasn't enrolled a passkey yet. Navigation to
          the dashboard is deferred until the user answers. */}
      {enrollPrompt && (
        <div className="passkey-prompt-overlay" role="dialog" aria-modal="true">
          <div className="passkey-prompt-card">
            <div className="passkey-prompt-icon">
              <svg width="42" height="42" viewBox="0 0 24 24" fill="none" stroke="#381F83" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 12v-2a5 5 0 0 0-10 0v2" />
                <path d="M12 18v-6" />
                <circle cx="12" cy="21" r="1" />
                <rect x="4" y="12" width="16" height="10" rx="2" />
              </svg>
            </div>
            <h3 className="passkey-prompt-title">Enable fingerprint sign-in?</h3>
            <p className="passkey-prompt-body">
              Skip typing your password next time — sign in with Touch ID / Windows Hello on this device.
            </p>
            {enrollError && (
              <p className="passkey-prompt-error">{enrollError}</p>
            )}
            <label className="passkey-prompt-remind">
              <input
                type="checkbox"
                checked={enrollDontAsk}
                onChange={(e) => setEnrollDontAsk(e.target.checked)}
                disabled={enrolling}
              />
              <span>Don't ask again</span>
            </label>
            <div className="passkey-prompt-actions">
              <button
                type="button"
                className="passkey-prompt-skip"
                onClick={handleEnrollSkip}
                disabled={enrolling}
              >
                Not now
              </button>
              <button
                type="button"
                className="passkey-prompt-primary"
                onClick={handleEnrollAccept}
                disabled={enrolling}
              >
                {enrolling ? 'Setting up…' : 'Enable'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginPage;
